import { ENTRY_ATTACHMENT_IMAGE_EXTENSIONS } from 'common/constants/file.upload.types';
import {
  ENTRY_ATTACHMENT_MAX_BYTES,
  ENTRY_ATTACHMENT_MAX_FILES_PER_SELECTION,
} from 'common/constants/file.upload.limits';
import { uploadEntryAttachment } from '@/api/resources/entryAttachment.api';
import { getErrorTk } from '@/utils/error.utils';
import { useToast } from '@/composables/useToast';
import { createImageThumbnail } from '@/utils/image.utils';
import type { IEntryAttachmentTarget } from 'common/enum/entry.attachment.enum';
import { useLocale } from '@/composables/useLocale';
import { formatFileSize, getAttachmentExtension } from 'common/utils/file.utils';
import { computed, onScopeDispose, ref } from 'vue';
import { useI18n } from 'vue-i18n';

export enum EPendingAttachmentStatus {
  Pending = 'pending',
  Uploading = 'uploading',
  Uploaded = 'uploaded',
}

export interface IPendingAttachment {
  id: number;
  file: File;
  imageUrl: string | null;
  status: EPendingAttachmentStatus;
}

export function useAttachmentUploads() {
  const { closableErrorToast, loadingToast } = useToast();
  const { t } = useI18n();
  const { formattingLocale } = useLocale();
  const pending = ref<IPendingAttachment[]>([]);
  const uploading = ref(false);
  const refreshFailed = ref(false);
  let nextId = 0;

  const remove = (id: number): void => {
    const item = pending.value.find((entry) => entry.id === id);
    if (!item || item.status === EPendingAttachmentStatus.Uploading) return;
    if (item.imageUrl) URL.revokeObjectURL(item.imageUrl);
    pending.value = pending.value.filter((entry) => entry.id !== id);
  };

  // File-specific reasons name the file; other backend errors keep their general message.
  const getUploadFailureKey = (error: unknown): string =>
    getErrorTk(error, {
      FILE_TYPE_INVALID: 'entryAttachment.invalidType',
      FILE_EMPTY: 'entryAttachment.empty',
      FILE_NAME_INVALID: 'entryAttachment.nameInvalid',
      FILE_SIZE_LIMIT_EXCEEDED: 'entryAttachment.tooLarge',
      FILE_VALIDATION_FAILED: 'entryAttachment.uploadFailed',
      UNKNOWN: 'entryAttachment.uploadFailed',
    });

  // One toast per reason: a single file is named, several are counted.
  const showFailures = (failures: Record<string, string[]>): void => {
    const size = formatFileSize(ENTRY_ATTACHMENT_MAX_BYTES, formattingLocale.value);
    for (const [key, names] of Object.entries(failures)) {
      closableErrorToast(t(key, { name: names[0], count: names.length, size }));
    }
  };

  const add = (files: File[]): void => {
    // the cap counts files still waiting, so a second selection cannot push a draft past it
    const room = ENTRY_ATTACHMENT_MAX_FILES_PER_SELECTION - pending.value.length;
    if (files.length > room) {
      closableErrorToast(t('entryAttachment.tooMany', { count: ENTRY_ATTACHMENT_MAX_FILES_PER_SELECTION }));
    }
    const failures: Record<string, string[]> = {};
    for (const file of files.slice(0, Math.max(room, 0))) {
      // Checked here because the browser hashes the whole file before the backend can reject it.
      if (file.size > ENTRY_ATTACHMENT_MAX_BYTES) {
        (failures['entryAttachment.tooLarge'] ??= []).push(file.name);
        continue;
      }
      const extension = getAttachmentExtension(file.name);
      pending.value.push({
        id: ++nextId,
        file,
        status: EPendingAttachmentStatus.Pending,
        imageUrl: ENTRY_ATTACHMENT_IMAGE_EXTENSIONS.includes(extension) ? URL.createObjectURL(file) : null,
      });
    }
    showFailures(failures);
  };

  /** Resolves to the failure's translation key, or null once the file is stored. */
  const uploadOne = async (item: IPendingAttachment, target: IEntryAttachmentTarget): Promise<string | null> => {
    item.status = EPendingAttachmentStatus.Uploading;
    try {
      // Only previewed images get one; a GIF thumbnail would freeze the animation on the card.
      const isGif = getAttachmentExtension(item.file.name) === '.gif';
      const thumbnail = item.imageUrl && !isGif ? await createImageThumbnail(item.file) : null;
      await uploadEntryAttachment(target, item.file, thumbnail);
      item.status = EPendingAttachmentStatus.Uploaded;
      return null;
    } catch (error) {
      item.status = EPendingAttachmentStatus.Pending;
      remove(item.id);
      return getUploadFailureKey(error);
    }
  };

  // A batch of only images says so; any document makes it a batch of files.
  const batchMessage = (key: 'uploading' | 'uploaded', items: IPendingAttachment[]): string =>
    t(`entryAttachment.${key}${items.every((item) => item.imageUrl) ? 'Images' : 'Files'}`, { count: items.length });

  const flush = async (target: IEntryAttachmentTarget, refresh?: () => Promise<void>): Promise<boolean> => {
    if (uploading.value) return false;
    uploading.value = true;
    const hadUploaded = pending.value.some((item) => item.status === EPendingAttachmentStatus.Uploaded);
    const queued = pending.value.filter((item) => item.status !== EPendingAttachmentStatus.Uploaded);
    const progress = queued.length ? loadingToast(batchMessage('uploading', queued)) : null;
    const uploaded: IPendingAttachment[] = [];
    const failures: Record<string, string[]> = {};
    try {
      // Preserve selection order when persisted attachments replace the local cards.
      for (const item of queued) {
        if (!pending.value.some((current) => current.id === item.id)) continue;
        const failure = await uploadOne(item, target);
        if (failure == null) uploaded.push(item);
        else (failures[failure] ??= []).push(item.file.name);
      }
      showFailures(failures);
      if (hadUploaded || uploaded.length) await refresh?.();
      for (const item of [...pending.value]) if (item.status === EPendingAttachmentStatus.Uploaded) remove(item.id);
      refreshFailed.value = false;
      return Object.keys(failures).length === 0;
    } catch {
      refreshFailed.value = true;
      closableErrorToast(t('entryAttachment.loadFailed'));
      return false;
    } finally {
      if (uploaded.length) progress?.succeed(batchMessage('uploaded', uploaded));
      else progress?.dismiss();
      uploading.value = false;
    }
  };

  const clear = (): void => {
    for (const item of [...pending.value]) remove(item.id);
    refreshFailed.value = false;
  };
  onScopeDispose(() => {
    for (const item of pending.value) if (item.imageUrl) URL.revokeObjectURL(item.imageUrl);
  });
  return {
    pending,
    uploading,
    refreshFailed,
    hasPending: computed(() => pending.value.length > 0),
    add,
    remove,
    flush,
    clear,
  };
}
